/**
 * Canvas Export Service (BYK-177)
 *
 * Provides export functionality for 2D floor plan and 3D scene.
 * Used for AI render reference images ("White Model" technique).
 *
 * Export Types:
 * - 2D Floor Plan: PNG export of Fabric.js canvas
 * - 3D White Model: PNG export of Babylon.js scene with neutral materials
 * - Combined: Side-by-side 2D + 3D for comprehensive reference
 */

import * as BABYLON from "@babylonjs/core";
import * as fabric from "fabric";

// ============================================================================
// Types
// ============================================================================

export interface CanvasExportOptions {
  /** Output width in pixels */
  width?: number;
  /** Output height in pixels */
  height?: number;
  /** Image format */
  format?: "png" | "jpeg";
  /** JPEG quality (0-1) */
  quality?: number;
  /** Background color (hex) */
  backgroundColor?: string;
}

export interface WhiteModelOptions extends CanvasExportOptions {
  /** Whether to remove textures and use flat white/gray materials */
  useWhiteModel?: boolean;
  /** Whether to show wireframe overlay */
  showWireframe?: boolean;
  /** Whether to include measurements/annotations */
  includeMeasurements?: boolean;
}

export interface ExportResult {
  /** Base64 encoded image data */
  dataUrl: string;
  /** Image width */
  width: number;
  /** Image height */
  height: number;
  /** File size in bytes (approximate) */
  sizeBytes: number;
}

export interface CombinedExportResult {
  /** 2D floor plan export */
  floorPlan: ExportResult | null;
  /** 3D white model export */
  whiteModel: ExportResult | null;
  /** Combined side-by-side image */
  combined: ExportResult | null;
}

// ============================================================================
// Default Options
// ============================================================================

const DEFAULT_EXPORT_OPTIONS: Required<CanvasExportOptions> = {
  width: 1920,
  height: 1080,
  format: "png",
  quality: 0.92,
  backgroundColor: "#ffffff",
};

const DEFAULT_WHITE_MODEL_OPTIONS: Required<WhiteModelOptions> = {
  ...DEFAULT_EXPORT_OPTIONS,
  useWhiteModel: true,
  showWireframe: false,
  includeMeasurements: true,
};

// ============================================================================
// 2D Floor Plan Export
// ============================================================================

/**
 * Export the 2D Fabric.js canvas as an image.
 */
export function export2DFloorPlan(
  canvas: fabric.Canvas,
  options: CanvasExportOptions = {}
): ExportResult {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };

  // Store original viewport for restoration
  const originalVpt = canvas.viewportTransform?.slice() as [
    number,
    number,
    number,
    number,
    number,
    number
  ];
  const originalWidth = canvas.getWidth();
  const originalHeight = canvas.getHeight();

  try {
    // Reset viewport to identity for clean export
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);

    // Calculate content bounds
    const objects = canvas.getObjects();
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    objects.forEach((obj) => {
      const bounds = obj.getBoundingRect();
      minX = Math.min(minX, bounds.left);
      minY = Math.min(minY, bounds.top);
      maxX = Math.max(maxX, bounds.left + bounds.width);
      maxY = Math.max(maxY, bounds.top + bounds.height);
    });

    // Add padding
    const padding = 50;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX += padding;
    maxY += padding;

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    // Calculate scale to fit in target size while maintaining aspect ratio
    const scale = Math.min(opts.width / contentWidth, opts.height / contentHeight);

    // Generate data URL with multiplier for resolution
    const dataUrl = canvas.toDataURL({
      format: opts.format === "jpeg" ? "jpeg" : "png",
      quality: opts.quality,
      multiplier: scale,
      left: minX,
      top: minY,
      width: contentWidth,
      height: contentHeight,
    });

    // Estimate size from base64 length
    const sizeBytes = Math.round((dataUrl.length * 3) / 4);

    return {
      dataUrl,
      width: Math.round(contentWidth * scale),
      height: Math.round(contentHeight * scale),
      sizeBytes,
    };
  } finally {
    // Restore original viewport
    if (originalVpt) {
      canvas.setViewportTransform(originalVpt);
    }
    canvas.setDimensions({ width: originalWidth, height: originalHeight });
    canvas.requestRenderAll();
  }
}

// ============================================================================
// 3D White Model Export
// ============================================================================

// Material cache for white model mode
const originalMaterials = new Map<BABYLON.AbstractMesh, BABYLON.Material | null>();

/**
 * Apply white model materials to all meshes in the scene.
 * Stores original materials for later restoration.
 */
function applyWhiteModelMaterials(scene: BABYLON.Scene): void {
  originalMaterials.clear();

  // Create shared white/gray materials
  const whiteMaterial = new BABYLON.StandardMaterial("whiteModelMat", scene);
  whiteMaterial.diffuseColor = new BABYLON.Color3(0.95, 0.95, 0.95);
  whiteMaterial.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
  whiteMaterial.emissiveColor = new BABYLON.Color3(0.05, 0.05, 0.05);

  const grayMaterial = new BABYLON.StandardMaterial("grayModelMat", scene);
  grayMaterial.diffuseColor = new BABYLON.Color3(0.7, 0.7, 0.7);
  grayMaterial.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);

  const floorMaterial = new BABYLON.StandardMaterial("floorModelMat", scene);
  floorMaterial.diffuseColor = new BABYLON.Color3(0.85, 0.85, 0.85);

  scene.meshes.forEach((mesh) => {
    if (!mesh.isVisible) return;

    // Store original material
    originalMaterials.set(mesh, mesh.material);

    // Apply appropriate white model material
    if (mesh.name === "floor") {
      mesh.material = floorMaterial;
    } else if (mesh.name.startsWith("wall-mesh-")) {
      mesh.material = grayMaterial;
    } else if (
      mesh.name.startsWith("volume-mesh-") ||
      mesh.name.startsWith("cabinet-")
    ) {
      mesh.material = whiteMaterial;
    } else if (mesh.name.startsWith("element-mesh-")) {
      // Windows and doors - slightly different shade
      const elementMat = new BABYLON.StandardMaterial(`elementMat-${mesh.id}`, scene);
      elementMat.diffuseColor = new BABYLON.Color3(0.8, 0.8, 0.85);
      elementMat.alpha = 0.9;
      mesh.material = elementMat;
    }
  });
}

/**
 * Restore original materials after white model export.
 */
function restoreOriginalMaterials(): void {
  originalMaterials.forEach((material, mesh) => {
    if (mesh && !mesh.isDisposed()) {
      mesh.material = material;
    }
  });
  originalMaterials.clear();
}

/**
 * Export the 3D Babylon.js scene as an image.
 * Optionally applies "White Model" technique for AI reference.
 */
export async function export3DWhiteModel(
  engine: BABYLON.Engine,
  scene: BABYLON.Scene,
  options: WhiteModelOptions = {}
): Promise<ExportResult> {
  const opts = { ...DEFAULT_WHITE_MODEL_OPTIONS, ...options };

  // Apply white model materials if requested
  if (opts.useWhiteModel) {
    applyWhiteModelMaterials(scene);
    // Render one frame with new materials
    scene.render();
  }

  return new Promise((resolve, reject) => {
    try {
      BABYLON.Tools.CreateScreenshot(
        engine,
        scene.activeCamera!,
        { width: opts.width, height: opts.height },
        (dataUrl) => {
          // Restore original materials
          if (opts.useWhiteModel) {
            restoreOriginalMaterials();
            scene.render();
          }

          // Estimate size from base64 length
          const sizeBytes = Math.round((dataUrl.length * 3) / 4);

          resolve({
            dataUrl,
            width: opts.width!,
            height: opts.height!,
            sizeBytes,
          });
        },
        opts.format === "jpeg" ? "image/jpeg" : "image/png"
      );
    } catch (error) {
      // Restore materials on error
      if (opts.useWhiteModel) {
        restoreOriginalMaterials();
      }
      reject(error);
    }
  });
}

/**
 * Export 3D scene with normal materials (for style reference).
 */
export async function export3DWithMaterials(
  engine: BABYLON.Engine,
  scene: BABYLON.Scene,
  options: CanvasExportOptions = {}
): Promise<ExportResult> {
  return export3DWhiteModel(engine, scene, {
    ...options,
    useWhiteModel: false,
  });
}

// ============================================================================
// Combined Export
// ============================================================================

/**
 * Combine two images side by side.
 */
export async function combineImages(
  leftImage: ExportResult,
  rightImage: ExportResult,
  options: CanvasExportOptions = {}
): Promise<ExportResult> {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };

  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Failed to get 2D context"));
      return;
    }

    // Calculate combined dimensions
    const combinedWidth = opts.width || leftImage.width + rightImage.width;
    const halfWidth = combinedWidth / 2;
    const height = opts.height || Math.max(leftImage.height, rightImage.height);

    canvas.width = combinedWidth;
    canvas.height = height;

    // Fill background
    ctx.fillStyle = opts.backgroundColor;
    ctx.fillRect(0, 0, combinedWidth, height);

    // Load and draw both images
    const leftImg = new Image();
    const rightImg = new Image();
    let loadedCount = 0;

    const checkComplete = () => {
      loadedCount++;
      if (loadedCount === 2) {
        // Draw left image (2D floor plan)
        const leftScale = Math.min(halfWidth / leftImg.width, height / leftImg.height);
        const leftDrawWidth = leftImg.width * leftScale;
        const leftDrawHeight = leftImg.height * leftScale;
        const leftX = (halfWidth - leftDrawWidth) / 2;
        const leftY = (height - leftDrawHeight) / 2;
        ctx.drawImage(leftImg, leftX, leftY, leftDrawWidth, leftDrawHeight);

        // Draw right image (3D white model)
        const rightScale = Math.min(halfWidth / rightImg.width, height / rightImg.height);
        const rightDrawWidth = rightImg.width * rightScale;
        const rightDrawHeight = rightImg.height * rightScale;
        const rightX = halfWidth + (halfWidth - rightDrawWidth) / 2;
        const rightY = (height - rightDrawHeight) / 2;
        ctx.drawImage(rightImg, rightX, rightY, rightDrawWidth, rightDrawHeight);

        // Draw divider line
        ctx.strokeStyle = "#e5e7eb";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(halfWidth, 0);
        ctx.lineTo(halfWidth, height);
        ctx.stroke();

        // Add labels
        ctx.font = "bold 14px Inter, sans-serif";
        ctx.fillStyle = "#6b7280";
        ctx.textAlign = "center";
        ctx.fillText("2D Floor Plan", halfWidth / 2, 24);
        ctx.fillText("3D White Model", halfWidth + halfWidth / 2, 24);

        // Export
        const dataUrl = canvas.toDataURL(
          opts.format === "jpeg" ? "image/jpeg" : "image/png",
          opts.quality
        );
        const sizeBytes = Math.round((dataUrl.length * 3) / 4);

        resolve({
          dataUrl,
          width: combinedWidth,
          height,
          sizeBytes,
        });
      }
    };

    leftImg.onload = checkComplete;
    rightImg.onload = checkComplete;
    leftImg.onerror = () => reject(new Error("Failed to load left image"));
    rightImg.onerror = () => reject(new Error("Failed to load right image"));

    leftImg.src = leftImage.dataUrl;
    rightImg.src = rightImage.dataUrl;
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert data URL to Blob for upload.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(",");
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * Download export result as a file.
 */
export function downloadExport(result: ExportResult, filename: string): void {
  const link = document.createElement("a");
  link.href = result.dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Format file size for display.
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
